import { Link } from 'react-router-dom';

interface Crumb {
  label: string;
  to?: string;
}

interface PageHeroProps {
  eyebrow: string;
  title: string;
  description?: string;
  crumbs: Crumb[];
  image?: string;
}

export default function PageHero({ eyebrow, title, description, crumbs, image }: PageHeroProps) {
  return (
    <section className="relative w-full overflow-hidden bg-primary-950">
      {image ? (
        <>
          <img
            src={image}
            alt={title}
            title={title}
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/80 to-primary-950/55"></div>
        </>
      ) : (
        <>
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-900 to-primary-800"></div>
          <div className="pointer-events-none absolute top-0 right-0 w-96 h-96 rounded-full bg-primary-700/40 blur-3xl"></div>
          <div className="pointer-events-none absolute bottom-0 left-10 w-72 h-72 rounded-full bg-secondary-700/30 blur-3xl"></div>
        </>
      )}

      <div className="relative z-10 w-full px-4 md:px-8 py-14 md:py-20">
        <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-2 text-xs md:text-sm text-background-100/75">
          {crumbs.map((crumb, index) => (
            <span key={`${crumb.label}-${index}`} className="flex items-center gap-2">
              {crumb.to ? (
                <Link to={crumb.to} className="hover:text-accent-300 transition-colors cursor-pointer whitespace-nowrap">
                  {crumb.label}
                </Link>
              ) : (
                <span className="text-accent-300 font-semibold whitespace-nowrap">{crumb.label}</span>
              )}
              {index < crumbs.length - 1 ? <i className="ri-arrow-right-s-line text-background-100/50"></i> : null}
            </span>
          ))}
        </nav>

        <span className="mt-6 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-accent-300">
          <span className="w-6 h-px bg-accent-300"></span>
          {eyebrow}
        </span>

        <h1 className="font-heading mt-3 text-3xl md:text-5xl font-semibold leading-tight text-background-50 max-w-4xl">
          {title}
        </h1>

        {description ? (
          <p className="mt-4 text-sm md:text-base leading-relaxed text-background-100/85 max-w-2xl">{description}</p>
        ) : null}
      </div>
    </section>
  );
}